import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";
import { downloadFile } from "@src/utils/hooks/useDownloadFile";
import { getDownloadURL, getMetadata, getStorage, ref, uploadString } from "firebase/storage";
import { Tag } from "../../../data-hooks/company/tags/tags.type";
import { DataCreator } from "../../../utils/dataCreator";
import { Utilities } from "../../../utils/utilities";
import { ReadAccess } from "../General/AccessData";
import { CreatedByData } from "../General/CreatedByData";
import { ExternalFileMetadata } from "../General/DataObjectHelpers/FileDocumentDataHelper/FileDocumentDataHelperUtilities";
import { FirebaseData } from "../General/FirebaseData";
import { LocalData } from "../General/LocalData";
import { RelativeData } from "../General/RelativeData";
import { FirebaseDataExternalReference } from "../Property/Units/FirebaseDataExternalReference";
import { Role } from "../User/Role";
import { UserData } from "../User/UserData";
import { DataFieldServiceAgreementType, DataFieldServiceReportType, DocumentAIType } from "./GlobalFileTypes";
import { IDocumentAIClassification, IFileDataFields } from "@propely/propely-typings";

export interface PropelyAIDeviation {
  id: string;
  title: string;
  description: string;
  rejected: boolean;
  priority?: string;
  status?: string;
  solvedDate?: string;
  deadline?: string;
  deviationID?: string;
  propertyID?: string;
}

export type processingState = "unprocessed" | "processing" | "processed" | "saved" | "error";

export class GlobalFileData extends FirebaseData {
  name = "";
  type = "";
  storagePath = "";
  previewPath?: string;
  size?: number;
  externalURL?: string;
  dataURL?: string;
  fileURL?: string;
  fileLoading = false;
  fileLoaded = false;
  fileLoadedListeners: (() => void)[] = [];
  tags: Tag[] = [];
  readAccess: ReadAccess = {};
  archived = false;
  relatives: RelativeData[] = [];
  externalFileClientID = "";
  externalRefs: { [key: string]: FirebaseDataExternalReference } = {};
  externalFileMetadata: ExternalFileMetadata = {};
  contentSummary?: string;
  documentType?: DocumentAIType;
  selectedClassification?: IDocumentAIClassification;
  classifications: IDocumentAIClassification[] = [];
  processingState: "unprocessed" | "processing" | "processed" | "saved" | "error" = "unprocessed";
  propertyIDs: string[] = [];
  dataFields?: IFileDataFields;

  setup(data: any): void {
    this.name = typeof data.name === "string" ? data.name : "";
    this.type = typeof data.type === "string" ? data.type : "";
    this.storagePath = typeof data.storagePath === "string" ? data.storagePath : "";
    this.previewPath = typeof data.previewPath === "string" ? data.previewPath : undefined;
    this.readAccess = Utilities.getReadAccess(data.readAccess);
    this.size = typeof data.size === "number" ? data.size : undefined;
    this.externalURL = typeof data.externalURL === "string" ? data.externalURL : undefined;
    this.archived = typeof data.archived === "boolean" ? data.archived : false;
    this.externalFileClientID = data.externalFileClientID || "";

    this.contentSummary = typeof data.contentSummary === "string" ? data.contentSummary : "";
    this.classifications = Array.isArray(data.classifications) ? data.classifications : [];
    this.classifications && this.classifications.length > 0 && (this.selectedClassification = this.classifications[0]);
    this.documentType = typeof data.documentType === "string" ? data.documentType : "other";
    this.processingState = typeof data.processingState === "string" ? data.processingState : "unprocessed";
    this.propertyIDs = Array.isArray(data.propertyIDs) ? data.propertyIDs : [];

    this.relatives = [];
    if (typeof data.relatives === "object") {
      for (const relativeID of Object.keys(data.relatives)) {
        this.relatives.push(new RelativeData(relativeID, data.relatives[relativeID]));
      }
    }

    if (typeof data.externalFileMetadata === "object") {
      this.externalFileMetadata = {
        propertyID: data.externalFileMetadata.propertyID || "",
      };
    }

    if (typeof data.externalRefs === "object") {
      this.externalRefs = {};
      for (const apiClientID of Object.keys(data.externalRefs)) {
        this.externalRefs[apiClientID] = {
          id: typeof data.externalRefs[apiClientID].id === "string" ? data.externalRefs[apiClientID].id : "",
          apiClientID,
        };
      }
    }

    this.tags = [];
    if (Array.isArray(data.tags)) {
      for (const tagID of data.tags) {
        const tag = LocalData.getTagWithId(tagID);
        if (tag) this.tags.push(tag);
      }
    }

    this.readAccess = Utilities.getReadAccess(data.readAccess);

    if (typeof data.selectedClassification === "object" && data.selectedClassification !== null) {
      this.selectedClassification = data.selectedClassification;
    } else if (Array.isArray(data.classifications) && this.selectedClassification === undefined) {
      const classificationsArray: IDocumentAIClassification[] = data.classifications;
      if (classificationsArray.length > 0) this.selectedClassification = classificationsArray[0];
    }

    if (this.documentType === "report") {
      // Narrowing down the type
      const fields: DataFieldServiceReportType = data.dataFields ?? {};

      const executedDate: Date | undefined = Utilities.getDateFromTimestamp(fields?.executedDate);
      if (executedDate) {
        fields.executedDate = executedDate;
      }
      this.dataFields = fields;
    } else if (this.documentType === "agreement") {
      const fields: DataFieldServiceAgreementType = data.dataFields ?? {};
      const agreementStartDate: Date | undefined = Utilities.getDateFromTimestamp(fields?.agreementStartDate);
      const agreementEndDate: Date | undefined = Utilities.getDateFromTimestamp(fields?.agreementEndDate);
      const agreementSignDate: Date | undefined = Utilities.getDateFromTimestamp(fields?.agreementSignDate);
      if (agreementStartDate) {
        fields.agreementStartDate = agreementStartDate;
      }
      if (agreementEndDate) {
        fields.agreementEndDate = agreementEndDate;
      }
      if (agreementSignDate) {
        fields.agreementSignDate = agreementSignDate;
      }
      this.dataFields = fields;
    }
    super.setup(data);
  }

  rename(name: string) {
    return this.saveUpdate({
      name,
    });
  }

  loadFileURL(callback: () => void): void {
    this.fileLoadedListeners.push(callback);
    const storage = getStorage();
    if (!this.fileLoading && this.storagePath) {
      const reference = ref(storage, this.storagePath);
      this.fileLoading = true;
      getDownloadURL(reference)
        .then((url) => {
          if (typeof url === "string") {
            this.fileURL = url;
          } else {
            this.deleted = true;
          }
        })
        .catch(() => {
          this.deleted = true;
        })
        .finally(() => {
          this.fileLoading = false;
          this.fileLoaded = true;
          for (const listener of this.fileLoadedListeners) {
            listener();
          }
        });
    }
  }

  loadStorageMetadata(callback: () => void) {
    const storage = getStorage();

    if (this.storagePath) {
      const reference = ref(storage, this.storagePath);

      getMetadata(reference)
        .then((metadata) => {
          if (!this.size && typeof metadata.size === "number") this.size = metadata.size;
          if (!this.createdBy?.date && typeof metadata.timeCreated === "string") {
            if (!this.createdBy) this.createdBy = { date: new Date(metadata.timeCreated) };
            else if (this.createdBy) this.createdBy.date = new Date(metadata.timeCreated);
          }
        })
        .finally(() => {
          callback();
        });
    }
  }

  deleteFile() {
    return this.saveUpdate({
      deletedBy: DataCreator.getDeletedBy(),
    });
  }

  downloadFile(): Promise<any> {
    return downloadFile(this, undefined);
  }

  archive() {
    const newArchiveStatus = !this.archived;
    return this.saveUpdate({ archived: newArchiveStatus }).then(() => {
      this.archived = newArchiveStatus;
    });
  }

  hasWriteAccess(): boolean {
    const rolesWithEditAccess = [Role.LANDLORD, Role.ENTREPRENEUR];
    const properties = LocalData.currentUser.properties.filter((property) => this.propertyIDs.includes(property.id));
    return (
      this.readAccess.users?.includes(LocalData.currentUser) ||
      Boolean(LocalData.currentUser.companies.find((company) => this.readAccess.companies?.includes(company))) ||
      properties.some((property) => property.members.getData(LocalData.currentUser.id)?.roles.some((role) => rolesWithEditAccess.includes(role)))
    );
  }

  getFirebaseMap(): { [key: string]: any } {
    const returnData: { [key: string]: any } = {
      type: this.type,
      name: this.name,
    };
    if (this.storagePath) returnData.storagePath = this.storagePath;
    if (this.size) returnData.size = this.size;
    if (this.externalURL) returnData.externalURL = this.externalURL;
    if (this.propertyIDs) returnData.propertyIDs = this.propertyIDs;
    if (this.createdBy) {
      returnData.createdBy = {};
      if (this.createdBy.user) returnData.createdBy.userID = this.createdBy.user.id;
      if (this.createdBy.date) returnData.createdBy.date = this.createdBy.date;
    }
    return returnData;
  }

  saveAndLoadURL(contentType?: string) {
    const saveURL = this.dataURL ?? this.fileURL;
    if (saveURL && this.storagePath) {
      const storage = getStorage();
      const reference = ref(storage, this.storagePath);
      return uploadString(reference, saveURL, "data_url", contentType ? { contentType } : undefined)
        .then(async () => {
          this.loaded = true;
          this.fileURL = await getDownloadURL(reference);
          return this;
        })
        .catch((error) => {
          console.log("Error saving file " + error);
          ToastUtilities.presentError(error.code + ": " + error.message);
        });
    } else {
      throw new Error("No file to save");
    }
  }

  setCreatedBy(createdBy: CreatedByData | undefined, user: UserData | undefined, date: Date | undefined) {
    this.createdBy = {
      date: createdBy?.date ?? date,
      user: createdBy?.user ?? user,
    };
  }
}
